import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrgId } from '~/hooks/org'
import { useQueryWrapper } from '~/hooks/useQueryWrapper'
import { useServerRequests } from '~/hooks/useRequests'
import { Automation } from '~/types/automations'

export const useAutomations = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const queryClient = useQueryClient()
  const { serverGetRequest } = useServerRequests()
  const activeOrgId = useActiveOrgId()

  const fetchAutomations = useCallback(async () => {
    if (!activeOrgId) {
      return { automations: [] }
    }

    const response = await serverGetRequest<{ automations: Automation[] }>({
      path: '/api/org/automations',
      params: {
        orgId: activeOrgId,
      },
    })

    // Pre-populate individual automation cache entries
    response?.data?.automations?.forEach((automation) => {
      const queryKey = [QUERY_KEYS.AUTOMATION, automation.id]
      const queryData = queryClient.getQueryData(queryKey)

      if (!queryData) {
        queryClient.setQueryData(queryKey, { automation })
      }
    })

    return response?.data
  }, [serverGetRequest, activeOrgId, queryClient])

  const {
    data: responseData,
    isLoading,
    isSuccess,
  } = useQueryWrapper<{
    automations: Automation[]
  }>({
    queryKey: [QUERY_KEYS.AUTOMATIONS, activeOrgId],
    queryFn: fetchAutomations,
    enabled: !!activeOrgId && enabled,
  })

  return {
    automations: responseData?.automations ?? [],
    isLoading,
    isSuccess,
  }
}
